import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatDateID } from "@/lib/format";
import { toast } from "sonner";
import { usePager, Pager } from "@/components/kiyana/Pager";
import { Plus, X, Megaphone, Pencil, Trash2, Info } from "lucide-react";
import EmpPicker from "@/components/EmpPicker";
import { MAROON, inpL } from "@/components/kiyana/LeaveForm";

const card = "bg-white rounded-2xl border border-slate-100";

function AnnForm({ initial, roles, employees, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({ title: initial?.title || "", body: initial?.body || "", target_roles: initial?.target_roles || [], target_user_ids: initial?.target_user_ids || [], is_active: initial?.is_active ?? true });
  const [busy, setBusy] = useState(false);
  const toggleRole = (rid) => setForm((f) => ({ ...f, target_roles: f.target_roles.includes(rid) ? f.target_roles.filter((r) => r !== rid) : [...f.target_roles, rid] }));

  const submit = async () => {
    if (form.title.trim().length < 3) return toast.error("Judul pengumuman wajib diisi");
    if (form.body.trim().length < 3) return toast.error("Isi pengumuman wajib diisi");
    setBusy(true);
    try {
      const payload = { title: form.title.trim(), body: form.body.trim(), target_roles: form.target_roles, target_user_ids: form.target_user_ids, is_active: form.is_active };
      if (isEdit) await api.put(`/admin/announcements/${initial.id}`, payload);
      else await api.post("/admin/announcements", payload);
      toast.success(isEdit ? "Pengumuman diperbarui" : "Pengumuman diterbitkan ke portal karyawan");
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="annf-modal" className="fixed inset-0 z-50 bg-slate-900/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="font-display text-base font-bold text-slate-900">{isEdit ? "Edit Pengumuman" : "Buat Pengumuman"}</h3>
          <button data-testid="annf-close" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-50 flex items-center justify-center text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Judul <span className="text-red-500">*</span></label>
            <input data-testid="annf-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="mis. Toko tutup saat Idul Adha" className={inpL} />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Isi Pengumuman <span className="text-red-500">*</span></label>
            <textarea data-testid="annf-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} placeholder="Tulis isi pengumuman..." className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#7F1D2D]" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Ditujukan untuk role</label>
            <div className="flex gap-1.5 flex-wrap mt-1">
              <button data-testid="annf-role-all" onClick={() => setForm({ ...form, target_roles: [], target_user_ids: [] })}
                className={`h-8 px-3 rounded-full text-[11px] font-semibold border transition-colors ${!form.target_roles.length && !form.target_user_ids.length ? "border-[#7F1D2D] bg-[#7F1D2D] text-white" : "border-slate-200 text-slate-500"}`}>Semua Karyawan</button>
              {roles.map((r) => (
                <button key={r.id} data-testid={`annf-role-${r.id}`} onClick={() => toggleRole(r.id)}
                  className={`h-8 px-3 rounded-full text-[11px] font-semibold border transition-colors ${form.target_roles.includes(r.id) ? "border-[#7F1D2D] bg-[#7F1D2D] text-white" : "border-slate-200 text-slate-500"}`}>{r.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Atau karyawan tertentu (bisa lebih dari satu)</label>
            <EmpPicker tid="annf-emp" options={employees} value={form.target_user_ids} onChange={(v) => setForm({ ...form, target_user_ids: v })} placeholder="Ketik nama karyawan" />
            <p className="text-[10px] text-slate-400 mt-1.5">Pilih role, karyawan tertentu, atau keduanya — kosongkan semua untuk "Semua Karyawan".</p>
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <button type="button" data-testid="annf-active" onClick={() => setForm({ ...form, is_active: !form.is_active })}
              className={`w-10 h-6 rounded-full transition-colors relative ${form.is_active ? "bg-[#7F1D2D]" : "bg-slate-200"}`}>
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${form.is_active ? "left-5" : "left-1"}`} />
            </button>
            <span className="text-xs font-semibold text-slate-600">{form.is_active ? "Aktif — langsung tampil di portal karyawan" : "Nonaktif — disimpan sebagai draf"}</span>
          </label>
          <button data-testid="annf-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-xl text-white text-sm font-semibold disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Menyimpan..." : isEdit ? "Simpan Perubahan" : "Terbitkan Pengumuman"}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminAnnouncements() {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [delId, setDelId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/announcements").then((r) => setData(r.data)).catch((e) => { setData(null); toast.error(e?.response?.data?.detail || "Gagal memuat pengumuman"); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const { paged: pagedAnn, pager: annPager } = usePager(items);
  const roles = data?.options?.roles || [];
  const employees = data?.options?.employees || [];
  const roleLabel = Object.fromEntries(roles.map((r) => [r.id, r.label]));
  const empName = Object.fromEntries(employees.map((e) => [e.id, e.name]));

  const toggleActive = async (a) => {
    try {
      await api.put(`/admin/announcements/${a.id}`, { title: a.title, body: a.body, target_roles: a.target_roles, target_user_ids: a.target_user_ids || [], is_active: !a.is_active });
      toast.success(a.is_active ? "Pengumuman dinonaktifkan" : "Pengumuman diaktifkan");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah status"); }
  };

  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/announcements/${delId}`);
      toast.success("Pengumuman dihapus");
      setDelId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="ann-page" className="space-y-5 pb-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Pengumuman</h1>
          <p className="text-xs text-slate-400 mt-0.5">Pengumuman aktif tampil di beranda portal karyawan sesuai role yang dituju.</p>
        </div>
        <button data-testid="ann-create-btn" onClick={() => { setEditItem(null); setShowForm(true); }} className="h-10 px-4 rounded-xl text-white text-sm font-semibold flex items-center gap-1.5 transition-colors" style={{ background: MAROON }}><Plus className="w-4 h-4" /> Buat Pengumuman</button>
      </div>

      <div className="flex items-center gap-2 text-[11px] text-sky-700 bg-sky-50 border border-sky-100 rounded-xl p-3">
        <Info className="w-3.5 h-3.5 flex-shrink-0" /> Pengumuman dengan target "Semua Role" tampil untuk seluruh karyawan; target role tertentu hanya tampil untuk karyawan di role tersebut. Pelanggan toko online tidak melihat pengumuman.
      </div>

      <div className="space-y-3">
        {!data && <div className="py-10 text-center text-xs text-slate-400">Memuat...</div>}
        {data && !items.length && <div data-testid="ann-empty" className={`${card} py-12 text-center text-xs text-slate-400`}>Belum ada pengumuman. Klik "Buat Pengumuman" untuk memulai.</div>}
        {pagedAnn.map((a) => (
          <div key={a.id} data-testid={`ann-row-${a.id}`} className={`${card} p-4 flex items-start gap-3`}>
            <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${a.is_active ? "bg-[#FBF3F4] text-[#7F1D2D]" : "bg-slate-100 text-slate-400"}`}><Megaphone className="w-5 h-5" /></span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold text-slate-900">{a.title}</span>
                <span data-testid={`ann-status-${a.id}`} className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${a.is_active ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>{a.is_active ? "Aktif" : "Nonaktif"}</span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{a.body}</p>
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {!a.target_roles.length && !(a.target_user_ids || []).length
                  ? <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#FBF3F4] text-[#7F1D2D] font-semibold">Semua Karyawan</span>
                  : <>
                      {a.target_roles.map((r) => <span key={r} className="text-[9px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">{roleLabel[r] || r}</span>)}
                      {(a.target_user_ids || []).map((u) => <span key={u} className="text-[9px] px-2 py-0.5 rounded-full bg-[#FBF3F4] text-[#7F1D2D] font-semibold">{empName[u] || "Karyawan"}</span>)}
                    </>}
                <span className="text-[9px] text-slate-400">• {formatDateID((a.created_at || "").slice(0, 10))} oleh {a.created_by || "—"}</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button data-testid={`ann-toggle-${a.id}`} onClick={() => toggleActive(a)} className="h-8 px-3 rounded-lg border border-slate-200 text-[11px] font-semibold text-slate-500 hover:border-[#7F1D2D]/30 hover:text-[#7F1D2D] transition-colors">{a.is_active ? "Nonaktifkan" : "Aktifkan"}</button>
              <button data-testid={`ann-edit-${a.id}`} onClick={() => { setEditItem(a); setShowForm(true); }} className="w-8 h-8 rounded-lg border border-slate-200 text-slate-500 hover:text-[#7F1D2D] hover:border-[#7F1D2D]/30 flex items-center justify-center transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
              <button data-testid={`ann-del-${a.id}`} onClick={() => setDelId(a.id)} className="w-8 h-8 rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-200 flex items-center justify-center transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
        <Pager {...annPager} testid="ann-pager" />
      </div>

      {showForm && <AnnForm initial={editItem} roles={roles} employees={employees} onClose={() => setShowForm(false)} onSaved={load} />}
      {delId && (
        <div data-testid="ann-del-modal" className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-6" onClick={() => setDelId(null)}>
          <div className="bg-white w-full max-w-xs rounded-2xl p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-base font-bold text-slate-900">Hapus Pengumuman?</h3>
            <p className="text-xs text-slate-500">Pengumuman yang dihapus tidak bisa dikembalikan dan langsung hilang dari portal karyawan.</p>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="ann-del-no" onClick={() => setDelId(null)} className="h-10 rounded-xl border border-slate-200 text-slate-500 text-xs font-semibold">Batal</button>
              <button data-testid="ann-del-yes" onClick={del} disabled={busy} className="h-10 rounded-xl bg-red-500 text-white text-xs font-semibold disabled:opacity-40">{busy ? "Menghapus..." : "Ya, Hapus"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
